import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { clearStateID } from "../state/stateSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import ButtonCreate from "../../components/drawer/ButtonCreate";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import { setGiftCodeID } from "./giftcodeSlice";
import {
  deleteGiftCode,
  getAllGiftCodes,
  giftcodeActiveInactive,
} from "./services/giftcode.services";
import { useNavigate } from "react-router-dom";

const GiftCode: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  useEffect(() => {
    dispatch(getAllGiftCodes());
    return () => {
      dispatch(closeDialog());
    };
  }, []);
  return (
    <>
      <DeleteGIftCodeDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Gift Code List
          </Typography.h4>
          <div>
            <Button
              onClick={() => navigate("/giftcodeledger")}
              fillMode={"solid"}
              themeColor={"primary"}
              className="me-2"
            >
              {"Ledger Report"}
            </Button>
            {isAddAccess && (
              <ButtonCreate
                label="Create Gift Code"
                drawerName="giftcodeForm"
                drawerWidth={600}
              />
            )}
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <GiftCodeGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const GiftCodeGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const GiftCodeList = useAppSelector((state) => state.giftcode.GiftCodeList);
  const loading = useAppSelector((state) => state.giftcode.loading);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? GiftCodeList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: any) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteGiftCode"));
      dispatch(setGiftCodeID(ID));
    };
    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {!props.dataItem.IsUsed && isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.ID}
            drawerName="giftcodeForm"
            drawerWidth={600}
          />
        )}
        {!props.dataItem.IsUsed && isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 15 || props.index === 16 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(giftcodeActiveInactive(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllGiftCodes());
        } else {
          dispatch(getAllGiftCodes());
        }
      });
    };

    return (
      <td
        {...props.tdProps}
        className={props.className}
        style={{ ...props.tdProps.style, textAlign: "center" }}
      >
        {!props.dataItem.IsUsed && (
          <Switch
            disabled={isEditAccess ? false : true}
            name="IsActive"
            checked={IsActive}
            onChange={handleActiveInActive}
            size="small"
          />
        )}
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {GiftCodeList && GiftCodeList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          scrollable="scrollable"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(GiftCodeList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : GiftCodeList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={GiftCodeList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
          className="responsive-table"
        >
          <Column
            field="GiftCode"
            title="Gift Code"
            cells={{
              data: (props: any) => (
                <td
                  className={`${isEditAccess ? "hoverableName" : "normalName"}`}
                >
                  {isEditAccess ? (
                    <ButtonEdit
                      ID={props.dataItem.ID}
                      drawerName="giftcodeForm"
                      label={props.dataItem?.GiftCode}
                      drawerWidth={600}
                    />
                  ) : (
                    props.dataItem?.GiftCode
                  )}
                </td>
              ),
            }}
            width={130}
          />
          <Column
            field="Amount"
            title="Amount"
            width={130}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  ₹ {formatIndianNumber(props.dataItem.Amount)}
                </td>
              ),
            }}
          />
          <Column field="UserMobile" title="Mobile" width={110} />
          <Column field="AssignUser" title="Assigned User" width={150} />
          <Column field="FromDate" title="From Date" width={100} />
          <Column field="ToDate" title="To Date" width={100} />
          <Column
            field="OnlyUsedInWallet"
            title="Only For Wallet"
            width={130}
            cells={{
              data: (props) => (
                <td className="text-center">
                  {props.dataItem?.OnlyUsedInWallet?.toString()}
                </td>
              ),
            }}
          />
          <Column
            field="UsageCount"
            title="Usage Count"
            width={110}
            cells={{
              data: (props) => (
                <td className="text-center fw-bold">
                  {props.dataItem.UsageCount}
                </td>
              ),
            }}
          />
          <Column
            field="MinAmtUsage"
            title="Min Amt"
            width={120}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  {props.dataItem.MinAmtUsage
                    ? "₹ " + formatIndianNumber(props.dataItem.MinAmtUsage)
                    : ""}
                </td>
              ),
            }}
          />
          <Column
            field="MaxAmtUsage"
            title="Max Amt"
            width={120}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  {props.dataItem.MaxAmtUsage
                    ? "₹ " + formatIndianNumber(props.dataItem.MaxAmtUsage)
                    : ""}
                </td>
              ),
            }}
          />
          {/* <Column field="AmountType" title="Type" width={100} /> */}
          <Column
            field="FlightTicketAmt"
            title="Flight Amt"
            width={120}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  {props.dataItem?.AmountType === "RS" && "₹ "}
                  {formatIndianNumber(props.dataItem.FlightTicketAmt)}
                  {props.dataItem?.AmountType === "PER" && " %"}
                </td>
              ),
            }}
          />
          <Column
            field="HotelBookingAmt"
            title="Hotel Amt"
            width={120}
            cells={{
              data: (props) => (
                <td className="text-end fw-bold">
                  {props.dataItem?.AmountType === "RS" && "₹ "}
                  {formatIndianNumber(props.dataItem.HotelBookingAmt)}
                  {props.dataItem?.AmountType === "PER" && " %"}
                </td>
              ),
            }}
          />
          <Column
            field="IsUsed"
            title="Is Used"
            width={80}
            cells={{
              data: (props) => (
                <td className="text-center">
                  {props.dataItem?.IsUsed?.toString()}
                </td>
              ),
            }}
          />
          <Column field="UsedOn" title="Used On" width={100} />
          <Column field="UsedBy" title="Used By" width={150} />
          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInActtiveCommandCell }}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteGIftCodeDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const GiftCodeID = useAppSelector((state) => state.giftcode.GiftCodeID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteGiftCode(ID));
      if (response.payload?.Data?.Status === 200) {
        dispatch(getAllGiftCodes());
        dispatch(closeDialog());
        dispatch(clearStateID());
      } else {
        dispatch(getAllGiftCodes());
        dispatch(closeDialog());
        dispatch(clearStateID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteGiftCode" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Gift Code?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Gift Code?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(GiftCodeID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default GiftCode;
